// public/scripts/index-main.js

// Imports from existing shared modules
import { initKridaAuth, getFirebaseAuthService, getFunctionsService } from './krida-auth.js';
import { initNavDrawer, closeDrawer } from './nav-drawer-manager.js';
import { initRulesModal, openRulesModal } from './rules-modal.js';
import { initTutorialSystem, launchTutorial } from './krida-tutorial.js';
import { initScoringModal, openScoringModal } from './scoring-modal.js';
import { initHeader } from './shared-header.js';
import { ThemeManager } from './theme-manager.js';

// Imports from new modules
import { initializeAuthEventListeners } from './index-auth.js';
import { initPayment } from './payment-client.js';
import { initializeUIEventListenersAndUpdates, updateLeaderboardUI as initialLeaderboardUpdate, openModal as openUiModal, loadGameHistory as loadUiGameHistory, handleActionFromUrl, updatePlayerStatsUI, updateLeaderboardUI } from './index-ui.js';
import { initializeTournamentModal } from './tourny-create-modal.js';
import { initJoinGameModal } from './join-game-modal.js';
import { initJoinTournamentModal } from './tourny-join-modal.js';
import { displayMyGames } from './my-games-client.js';
// displayMyGames, fetchLeaderboardData, getPlayerStats are used within other modules now.


// --- Helper Function ---
// This function is used by index-auth.js, so it's exported.
export function getFirstName(displayName) {
  if (!displayName || typeof displayName !== 'string') {
      return "Player"; // Fallback
  }
  const nameParts = displayName.trim().split(/\s+/); // Split by whitespace
  return nameParts[0] || "Player";
}

// --- Function to load and initialize shared modals (Rules, Scoring) ---
function loadSharedModalContent() {
  // Load Rules Modal
  fetch('/rules-modal.html')
    .then(response => response.text())
    .then(data => {
      const container = document.getElementById('rulesModalContainer');
      if (container) container.innerHTML = data;
      // initRulesModal is called here after content is loaded.
      // Ensure rules-modal.js handles its own internal setup if needed upon initRulesModal call.
      initRulesModal(); 
    })
    .catch(error => console.error('Error loading rules modal:', error));

  // Load Scoring Modal
  fetch('/scoring-modal.html')
    .then(response => response.text())
    .then(data => {
      const container = document.getElementById('scoringModalContainer');
      if (container) container.innerHTML = data;
      // initScoringModal is called here.
      initScoringModal();
    })
    .catch(error => console.error('Error loading scoring modal:', error));
}


// --- DOMContentLoaded Event Listener ---
document.addEventListener('DOMContentLoaded', async () => {
    console.log("index-main.js: DOM Content Loaded.");

    // Initialize Firebase Auth first
    await initKridaAuth();

    // Initialize Header
    await initHeader();

    // Initialize Nav Drawer
    initNavDrawer([
        { id: 'nav-game-history-btn', text: 'Game History', handler: () => { closeDrawer(); loadUiGameHistory(); } }
    ]);

    // Initialize Modals
    initRulesModal();
    initScoringModal();
    initTutorialSystem();
    initializeTournamentModal();
    initJoinGameModal();
    initJoinTournamentModal();

    // Initialize Theme Manager with dashboard context
    ThemeManager.init('dashboard');

    // Initialize payment module
    const functions = getFunctionsService();
    if (functions) initPayment(functions);

    // Listen for new user registration to auto-launch walkthrough
    document.addEventListener('krida-new-user-registered', () => {
        launchTutorial('quickStart', true);
    });

    // Initialize Authentication Event Listeners
    initializeAuthEventListeners();

    // Initialize other UI components, event listeners, and initial data loading (stats, leaderboard, games)
    initializeUIEventListenersAndUpdates();

    // Check if user is already authenticated and update UI accordingly
    const auth = getFirebaseAuthService();
    if (auth && auth.currentUser) {
        console.log("User is already authenticated on page load, updating UI immediately");
        updatePlayerStatsUI(auth.currentUser.uid);
        displayMyGames();
        updateLeaderboardUI();
    }

    // NEW: Handle any actions passed in the URL (e.g., join a game, open a modal)
    handleActionFromUrl();

    // Initial fetch for leaderboard (will be updated by auth state changes for logged-in users)
    initialLeaderboardUpdate(); 

    console.log("index-main.js: DOMContentLoaded complete, initializations started.");
});

// Note: Functions previously on `window` (like openModal, closeModal, etc.) are now
// either exported from their respective modules and used internally,
// or event listeners are attached directly in JS instead of using HTML onclick attributes.
// If any HTML onclick attributes remain, they will need to be updated. 